import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import axios from "axios";
import Cookies from "js-cookie";
import classNames from 'classnames';
import { Box, Calendar, Clock, RefreshCw, Tag, Search, X, ArrowUpDown } from "lucide-react";
import './ListaModelosLlaveros.css';

// Componentes
import Modelo3dCard from "./Modelo3dCard.jsx";
import Modelo3dCardList from "./Modelo3dCardList.jsx";
import Modelo3dCardSkeleton from './Modelo3dCardSkeleton.jsx';
import CrearModeloButton from './CrearModeloButton.jsx';
import DropdownOrder from "../../../Store/DropdownOrder.jsx";
import DropdownTipoVista from "../../../Store/DropdownTipoVista.jsx";
import BuscadorInput from "../../../Store/BuscadorInput.jsx";

// Configuración inicial
const INITIAL_VIEW_STATE = {
    mode: "grid",
    columns: 3,
    sortBy: 'created_at',
    sortDirection: 'desc',
};

const SORT_OPTIONS = [
    {
        nombre: "Nombre",
        valor: "name",
        icon: <Tag className="w-4 h-4" />
    },
    {
        nombre: "Fecha de Creación",
        valor: "created_at",
        icon: <Calendar className="w-4 h-4" />
    },
    {
        nombre: "Última edición",
        valor: "modified_at",
        icon: <Clock className="w-4 h-4" />
    },
];

const VIEW_OPTIONS = [
    { label: '2 Columnas', value: { mode: 'grid', columns: 2 } },
    { label: '3 Columnas', value: { mode: 'grid', columns: 3 } },
    { label: '4 Columnas', value: { mode: 'grid', columns: 4 } },
    { label: 'Lista', value: { mode: 'list' } },
];

// Hook para debounce de búsqueda
const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => clearTimeout(handler);
    }, [value, delay]);

    return debouncedValue;
};

const ListaModelosLlaveros = () => {
    // Estados principales
    const [modelos3d, setModelos3d] = useState([]);
    const [totalCount, setTotalCount] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    // Estado de vista con persistencia
    const [viewMode, setViewMode] = useState(() => {
        const savedViewMode = Cookies.get('viewModeModelos3D');
        return savedViewMode ? JSON.parse(savedViewMode) : INITIAL_VIEW_STATE;
    });

    // Debounce para búsqueda
    const debouncedSearchTerm = useDebounce(searchTerm, 300);

    // Función principal de fetch
    const fetchModelos3d = useCallback(async (pageNum = 1, reset = false) => {
        try {
            setLoading(true);
            setError(null);

            const { data } = await axios.get("/customized-keyrings/", {
                params: {
                    page: pageNum,
                    sortBy: viewMode.sortBy,
                    sortDirection: viewMode.sortDirection,
                    search: debouncedSearchTerm || undefined
                }
            });

            setModelos3d(prev =>
                reset || pageNum === 1 ? data.results : [...prev, ...data.results]
            );
            setHasMore(Boolean(data.next));
            setTotalCount(data.count ?? null);
        } catch (err) {
            console.error("Error fetching modelos:", err);
            setError("Error al cargar los modelos");
        } finally {
            setLoading(false);
        }
    }, [viewMode.sortBy, viewMode.sortDirection, debouncedSearchTerm]);

    // Intersection Observer para scroll infinito
    const observer = useRef();
    const lastItemRef = useCallback(node => {
        if (loading) return;
        if (observer.current) observer.current.disconnect();

        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore && !loading) {
                setPage(prev => {
                    const nextPage = prev + 1;
                    fetchModelos3d(nextPage, false);
                    return nextPage;
                });
            }
        }, { threshold: 0.1 });

        if (node) observer.current.observe(node);
    }, [loading, hasMore, fetchModelos3d]);

    // Efectos
    useEffect(() => {
        setPage(1);
        fetchModelos3d(1, true);
    }, [viewMode, debouncedSearchTerm]);

    useEffect(() => {
        Cookies.set('viewModeModelos3D', JSON.stringify(viewMode), { expires: 7 });
    }, [viewMode]);

    // Handlers
    const handleDelete = useCallback((modeloId) => {
        setModelos3d(prev => prev.filter(modelo => modelo.id !== modeloId));
    }, []);

    const handleDuplicated = useCallback((newModelo) => {
        setModelos3d(prev => [newModelo, ...prev]);
        setTotalCount(prev => prev !== null ? prev + 1 : null);
    }, []);

    const handleRetry = useCallback(() => {
        setError(null);
        setPage(1);
        fetchModelos3d(1, true);
    }, [fetchModelos3d]);

    const handleClearSearch = useCallback(() => {
        setSearchTerm('');
    }, []);

    // Estilos memoizados para el grid
    const gridClassName = useMemo(() => {
        return viewMode.mode === "grid" ? "grid gap-4" : "flex flex-col space-y-3";
    }, [viewMode.mode]);

    const gridStyle = useMemo(() => {
        return viewMode.mode === "grid"
            ? { gridTemplateColumns: `repeat(${viewMode.columns}, minmax(0, 1fr))` }
            : {};
    }, [viewMode.mode, viewMode.columns]);

    // Estados de UI
    const showEmptyState = modelos3d.length === 0 && !loading && !error;
    const showSkeletons = loading && modelos3d.length === 0;
    const hasActiveFilters = debouncedSearchTerm || viewMode.sortBy !== 'created_at';

    return (
        <div className="min-h-full max-h-full flex flex-col gap-3">
            {/* Header de controles */}
            <div className="glassCard p-4 z-40">
                {/* Título y contador */}
                <div className="flex items-center justify-between mb-4 gap-3">
                    <div className="flex items-center space-x-3">
                        <Box className="w-6 h-6 text-primary" />
                        <div>
                            <h2 className="text-xl font-semibold">Modelos 3D</h2>
                            <p className="text-sm text-base-content/60">
                                {totalCount !== null
                                    ? `Mostrando ${modelos3d.length} de ${totalCount} ${totalCount === 1 ? 'modelo' : 'modelos'}`
                                    : `${modelos3d.length} ${modelos3d.length === 1 ? 'modelo' : 'modelos'}`
                                }
                            </p>
                        </div>
                    </div>
                    <CrearModeloButton size="sm" className="shrink-0" />
                </div>

                <div className="flex flex-col-reverse lg:flex-row gap-2">
                    {/* Búsqueda */}
                    <div className="flex-1 lg:flex-initial min-w-64 lg:min-w-48 lg:max-w-md">
                        <BuscadorInput
                            searchTerm={searchTerm}
                            setSearchTerm={setSearchTerm}
                            className="w-full"
                            placeholder="Buscar por nombre..."
                        />
                    </div>

                    {/* Controles de vista */}
                    <div className="grid grid-cols-2 gap-2 lg:flex lg:flex-row lg:items-center lg:gap-2">
                        <DropdownOrder
                            viewMode={viewMode}
                            setViewMode={setViewMode}
                            arrayDatosOrdenacion={SORT_OPTIONS}
                            className="w-full"
                        />
                        <DropdownTipoVista
                            options={VIEW_OPTIONS}
                            selectedValue={viewMode}
                            onChange={(mode) => setViewMode(prev => ({
                                ...prev,
                                mode: mode.mode,
                                columns: mode.columns
                            }))}
                            className="w-full"
                        />
                    </div>
                </div>

                {/* Pills de filtros activos */}
                {hasActiveFilters && (
                    <div className="mt-3 flex flex-wrap items-center gap-2">

                        {debouncedSearchTerm && (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1
                                             text-xs font-medium rounded-full
                                             bg-primary/10 text-primary border border-primary/20">
                                <Search className="w-3 h-3 shrink-0" />
                                <span className="max-w-[160px] truncate">"{debouncedSearchTerm}"</span>
                                <button
                                    onClick={handleClearSearch}
                                    className="ml-0.5 hover:text-error transition-colors"
                                    aria-label="Quitar filtro de búsqueda"
                                >
                                    <X className="w-3 h-3" />
                                </button>
                            </span>
                        )}

                        {viewMode.sortBy !== 'created_at' && (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1
                                             text-xs font-medium rounded-full
                                             bg-base-300 text-base-content/70 border border-base-300">
                                <ArrowUpDown className="w-3 h-3 shrink-0" />
                                <span>{SORT_OPTIONS.find(o => o.valor === viewMode.sortBy)?.nombre ?? 'Orden'}</span>
                                <button
                                    onClick={() => setViewMode(prev => ({ ...prev, sortBy: 'created_at', sortDirection: 'desc' }))}
                                    className="ml-0.5 hover:text-error transition-colors"
                                    aria-label="Restablecer orden"
                                >
                                    <X className="w-3 h-3" />
                                </button>
                            </span>
                        )}

                    </div>
                )}
            </div>

            {/* Contenido principal */}
            <div className={classNames(
                "h-full flex-1 overflow-y-auto scrollbar_mod glassCard",
                showEmptyState || error ? "flex items-center justify-center" : "p-4"
            )}>
                {/* Estado de error */}
                {error && (
                    <div className="text-center py-8">
                        <div className="w-16 h-16 mx-auto mb-4 bg-error/10 rounded-full flex items-center justify-center">
                            <RefreshCw className="w-8 h-8 text-error" />
                        </div>
                        <h3 className="text-lg font-medium text-base-content mb-2">
                            Error al cargar modelos
                        </h3>
                        <p className="text-base-content/60 mb-4">{error}</p>
                        <button onClick={handleRetry} className="btn btn-outline">
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Reintentar
                        </button>
                    </div>
                )}

                {/* Estado vacío */}
                {showEmptyState && (
                    <div className="text-center py-12">
                        <div className="w-20 h-20 mx-auto mb-6 bg-base-200 rounded-full flex items-center justify-center">
                            <Box className="w-10 h-10 text-base-content/30" />
                        </div>

                        {debouncedSearchTerm ? (
                            <>
                                <h3 className="text-lg font-medium text-base-content mb-2">
                                    No se encontraron modelos
                                </h3>
                                <p className="text-base-content/60 mb-6">
                                    No hay modelos que coincidan con "{debouncedSearchTerm}"
                                </p>
                                <button onClick={handleClearSearch} className="btn btn-outline">
                                    Limpiar búsqueda
                                </button>
                            </>
                        ) : (
                            <>
                                <h3 className="text-lg font-medium text-base-content mb-2">
                                    No tienes modelos 3D aún
                                </h3>
                                <p className="text-base-content/60 mb-6">
                                    Comienza creando tu primer modelo 3D
                                </p>
                                <div className="flex justify-center">
                                    <CrearModeloButton />
                                </div>
                            </>
                        )}
                    </div>
                )}

                {/* Grid/Lista de contenido */}
                {!error && !showEmptyState && (
                    <div className={gridClassName} style={gridStyle}>
                        {/* Skeletons durante carga inicial */}
                        {showSkeletons &&
                            Array.from({ length: viewMode.columns * 2 }).map((_, i) => (
                                <Modelo3dCardSkeleton key={`skeleton-${i}`} />
                            ))
                        }

                        {/* Modelos reales */}
                        {modelos3d.map((modelo, idx) => {
                            const isLast = idx === modelos3d.length - 1;
                            const Card = viewMode.mode === "grid" ? Modelo3dCard : Modelo3dCardList;

                            return isLast ? (
                                <div ref={lastItemRef} key={modelo.id}>
                                    <Card
                                        modelo={modelo}
                                        onDelete={() => handleDelete(modelo.id)}
                                        onDuplicated={handleDuplicated}
                                        sortBy={viewMode.sortBy}
                                    />
                                </div>
                            ) : (
                                <Card
                                    key={modelo.id}
                                    modelo={modelo}
                                    onDelete={() => handleDelete(modelo.id)}
                                    onDuplicated={handleDuplicated}
                                    sortBy={viewMode.sortBy}
                                />
                            );
                        })}

                        {/* Indicador de carga al final */}
                        {loading && modelos3d.length > 0 && (
                            <div className="col-span-full flex justify-center py-4">
                                <div className="flex items-center space-x-2 text-base-content/60">
                                    <div className="loading loading-spinner loading-sm"></div>
                                    <span className="text-sm">Cargando más modelos...</span>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Fin de contenido */}
                {!hasMore && modelos3d.length > 0 && !loading && (
                    <div className="text-center py-6 text-base-content/50">
                        <p className="text-sm">Has visto todos los modelos disponibles</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ListaModelosLlaveros;
